import type {
  BaseJobModuleNotificationPayload,
  JobModuleCompletedNotificationPayload,
  JobModuleFailedNotificationPayload,
  JobModuleRunningNotificationPayload,
  ModelDeleteAsyncFailedNotificationPayload,
  ModelDeleteAsyncModel,
  ModelDeleteAsyncNotificationData,
  ModelDeleteAsyncSuccessNotificationPayload,
  ObservationCommentedNotificationPayload,
} from "./broadcast-notification.type";
import type { JobModuleSubject } from "@/entities/job-module";

import { isValidObject } from "@/utilities/object";

import { BROADCAST_NOTIFICATION_TYPE } from "./broadcast-notification.enum";

/**
 * TYPES GUARDS
 */
export const isValidNamedModelResource = (
  payload: unknown
): payload is { id: number; name: string } =>
  isValidObject(payload) &&
  "id" in payload &&
  typeof payload.id === "number" &&
  "name" in payload &&
  typeof payload.name === "string";

export const isObservationCommentedNotificationPayload = (
  payload: unknown
): payload is ObservationCommentedNotificationPayload =>
  isValidObject(payload) &&
  "event_type" in payload &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.OBSERVATION_COMMENTED &&
  "intervention" in payload &&
  isValidNamedModelResource(payload.intervention) &&
  "observation" in payload &&
  isValidNamedModelResource(payload.observation) &&
  "comment" in payload &&
  isValidObject(payload.comment) &&
  "id" in payload.comment &&
  typeof payload.comment.id === "number" &&
  "author_name" in payload.comment &&
  typeof payload.comment.author_name === "string" &&
  "content" in payload.comment &&
  typeof payload.comment.content === "string";

export const isModelDeleteAsyncModel = (
  model: unknown
): model is ModelDeleteAsyncModel =>
  isValidObject(model) &&
  "id" in model &&
  typeof model.id === "number" &&
  "name" in model &&
  typeof model.name === "string" &&
  "type" in model &&
  typeof model.type === "string";

export const isModelDeleteAsyncNotificationData = (
  payload: unknown
): payload is ModelDeleteAsyncNotificationData =>
  isValidObject(payload) &&
  "models" in payload &&
  Array.isArray(payload.models) &&
  payload.models.every((model) => isModelDeleteAsyncModel(model)) &&
  "is_batch_processing" in payload &&
  typeof payload.is_batch_processing === "boolean";

export const isModelDeleteAsyncSuccessNotificationPayload = (
  payload: unknown
): payload is ModelDeleteAsyncSuccessNotificationPayload =>
  isModelDeleteAsyncNotificationData(payload) &&
  "event_type" in payload &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.ASYNC_DELETE_SUCCESS;

export const isModelDeleteAsyncFailedNotificationPayload = (
  payload: unknown
): payload is ModelDeleteAsyncFailedNotificationPayload =>
  isModelDeleteAsyncNotificationData(payload) &&
  "event_type" in payload &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.ASYNC_DELETE_FAILED;

export const isValidJobModuleSubjects = (
  subjects: unknown
): subjects is JobModuleSubject[] =>
  Array.isArray(subjects) &&
  subjects.every(isValidObject) &&
  subjects.every(
    (subject) => "id" in subject && typeof subject.id === "number"
  ) &&
  subjects.every(
    (subject) => "name" in subject && typeof subject.name === "string"
  ) &&
  subjects.every(
    (subject) => "type" in subject && typeof subject.type === "string"
  );

export const isJobModuleNotificationPayload = (
  payload: unknown
): payload is BaseJobModuleNotificationPayload =>
  isValidObject(payload) &&
  "event_type" in payload &&
  "job_module" in payload &&
  isValidObject(payload.job_module) &&
  "id" in payload.job_module &&
  typeof payload.job_module.id === "number" &&
  "module_name" in payload.job_module &&
  typeof payload.job_module.module_name === "string" &&
  "subjects" in payload.job_module &&
  isValidJobModuleSubjects(payload.job_module.subjects);

export const isJobModuleRunningNotificationPayload = (
  payload: unknown
): payload is JobModuleRunningNotificationPayload =>
  isJobModuleNotificationPayload(payload) &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.JOB_MODULE_RUNNING;

export const isJobModuleCompletedNotificationPayload = (
  payload: unknown
): payload is JobModuleCompletedNotificationPayload =>
  isJobModuleNotificationPayload(payload) &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.JOB_MODULE_COMPLETED;

export const isJobModuleFailedNotificationPayload = (
  payload: unknown
): payload is JobModuleFailedNotificationPayload =>
  isJobModuleNotificationPayload(payload) &&
  payload.event_type === BROADCAST_NOTIFICATION_TYPE.JOB_MODULE_FAILED;
